import { useCallback, useEffect } from 'react';
import { useLocalStorage } from './useLocalStorage';
import { usePrefersDarkMode } from './useMediaQuery';

type Theme = 'light' | 'dark' | 'system';

interface UseDarkModeReturn {
    theme: Theme;
    isDark: boolean;
    setTheme: (theme: Theme) => void;
    toggleTheme: () => void;
}

/**
 * Custom hook for dark mode management
 * Integrates with Tailwind CSS dark mode (class strategy)
 * Persists preference to localStorage and respects system preference
 */
export function useDarkMode(): UseDarkModeReturn {
    const prefersDark = usePrefersDarkMode();
    const [theme, setThemeState] = useLocalStorage<Theme>('theme', 'dark');

    // Calculate if dark mode should be active
    const isDark = theme === 'dark' || (theme === 'system' && prefersDark);

    // Update document class when dark mode changes
    useEffect(() => {
        const root = window.document.documentElement;

        if (isDark) {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
    }, [isDark]);

    const setTheme = useCallback((newTheme: Theme) => {
        setThemeState(newTheme);
    }, [setThemeState]);

    const toggleTheme = useCallback(() => {
        setThemeState((current) => {
            if (current === 'light') return 'dark';
            if (current === 'dark') return 'system';
            return 'light';
        });
    }, [setThemeState]);

    return { theme, isDark, setTheme, toggleTheme };
}

